import { NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { RouterLink } from '@angular/router';

import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';
import { JsonLdComponent } from '../../../shared/components/json-ld/json-ld.component';
import { JsonLd, JsonLdService } from '../../providers/json-ld.service';

export interface CollectionBreadcrumbDef {
    id: string;
    name: string;
    slug: string;
}

export interface ProductBreadcrumbDef {
    slug: string;
    name: string;
    __typename: 'Product';
}

export interface ProductVariantBreadcrumbDef {
    sku: string;
    name: string;
    __typename: 'ProductVariant';
}

export type BreadcrumbDef = CollectionBreadcrumbDef | ProductBreadcrumbDef | ProductVariantBreadcrumbDef;
export type BreadcrumbLink = {
    url: string;
    name: string;
};

/**
 * Generates a list of breadcrumb links ({@link BreadcrumbLink}[]) from breadcrumb definitions.
 * @param breadcrumbs {@link BreadcrumbDef}[]
 */
export function generateBreadcrumbList(breadcrumbs: BreadcrumbDef[]): BreadcrumbLink[] {
    let lastProductBreadcrumbDef: ProductBreadcrumbDef | null = null;
    return breadcrumbs
        .filter(notNullOrUndefined)
        .map((breadcrumb) => {
            if (!breadcrumb) { return undefined; }
            // Collection or Product breadcrumb
            if ('slug' in breadcrumb) {
                if ((breadcrumb as ProductBreadcrumbDef).__typename === 'Product') {
                    // Store the last Product breadcrumb to use it for the ProductVariant breadcrumb
                    lastProductBreadcrumbDef = breadcrumb as ProductBreadcrumbDef;
                    return {
                        url: `/p/${(breadcrumb as ProductBreadcrumbDef).slug}`,
                        name: breadcrumb.name,
                    };
                }
                return {
                    url: `/c/${(breadcrumb as CollectionBreadcrumbDef).slug}`,
                    name: breadcrumb.name,
                };
            } else if (
                (breadcrumb as ProductVariantBreadcrumbDef).__typename === 'ProductVariant' &&
                lastProductBreadcrumbDef
            ) {
                return {
                    url: `/p/${lastProductBreadcrumbDef.slug}/${
                        (breadcrumb as ProductVariantBreadcrumbDef).sku
                    }`,
                    name: (breadcrumb as ProductVariantBreadcrumbDef).sku,
                };
            }
        })
        .filter(notNullOrUndefined);
}

@Component({
    selector: 'vsf-collection-breadcrumbs',
    templateUrl: './collection-breadcrumbs.component.html',
    styleUrls: ['./collection-breadcrumbs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [RouterLink, NgFor, NgClass, NgIf, JsonLdComponent],
})
export class CollectionBreadcrumbsComponent implements OnChanges {
    @Input() breadcrumbs: BreadcrumbDef[] | null = [];
    @Input() linkLast = false;

    jsonLdData = signal<JsonLd | undefined>(undefined);
    breadcrumbLinks = signal<BreadcrumbLink[]>([]);

    constructor(private jsonldService: JsonLdService) {
    }

    tail<T>(arr: T[] | null): T[] {
        return arr ? arr.slice(1) : [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.breadcrumbs) {
            this.updateBreadcrumbs();
        }
    }

    private updateBreadcrumbs(): void {
        const breadcrumbs = this.tail(generateBreadcrumbList(this.breadcrumbs ?? []));
        this.breadcrumbLinks.set(breadcrumbs);
        this.jsonLdData.set(this.jsonldService.getBreadcrumbsData(breadcrumbs));
    }
}
